// Public self-signup uses an explicit, same-origin anonymous client.
 // PlatformApiClient is intentionally not reused: it requires an authenticated
 // session CSRF token for writes; these routes instead enforce trusted Origin.
export type SignupVerificationResult = Readonly<{
  verified: true;
  registrationTicket: string;
  ticketExpiresAt: string;
}>;

export type SignupAccountResult = Readonly<{
  userId: string;
  organizationId: string;
  organizationSlug: string;
  membershipId: string;
  created: boolean;
}>;

export class SignupRequestError extends Error {
  constructor(readonly status: number) {
    super("Signup request failed");
    this.name = "SignupRequestError";
  }
}
type Fetcher = typeof fetch;

export function verificationTokenFromFragment(fragment: string): string | null {
  // URLSearchParams accepts leading "?" as if it were a fragment.
  // Explicitly reject query syntax: verification secrets belong in a hash only.
  if (fragment.startsWith("?") || fragment.startsWith("#?")) return null;
  const hash = fragment.startsWith("#") ? fragment.slice(1) : fragment;
  const params = new URLSearchParams(hash);
  if ([...params.keys()].length !== 1 || !params.has("token")) return null;
  const token = params.get("token");
  return token && /^[A-Za-z0-9_-]{43}$/.test(token) ? token : null;
}

export class SignupApiClient {
  private readonly fetcher: Fetcher;
  constructor(fetcher: Fetcher = fetch) {
    // Native window.fetch must keep the global receiver; detached invocations
    // can fail in browsers even when synthetic fetch mocks pass.
    this.fetcher = fetcher.bind(globalThis);
  }

  private async post<T>(route: "requests" | "verify" | "complete", body: unknown): Promise<T> {
    const response = await this.fetcher(`/api/v1/platform/signup/${route}`, {
      method: "POST",
      mode: "same-origin",
      cache: "no-store",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new SignupRequestError(response.status);
    const result = await response.json() as { data?: T };
    if (!result || typeof result !== "object" || !("data" in result)) {
      throw new SignupRequestError(502);
    }
    return result.data as T;
  }

  request(email: string): Promise<{ accepted: true }> {
    return this.post("requests", { email });
  }
  verify(token: string): Promise<SignupVerificationResult> {
    return this.post("verify", { token });
  }
  complete(input: { registrationTicket: string; displayName: string; organizationName: string; password: string }): Promise<SignupAccountResult> {
    return this.post("complete", input);
  }
}
